import TableWrapper from "components/common/table-wrapper";
import { Pencil, Plus, Trash } from "phosphor-react";
import { useCallback, useEffect, useState } from "react";
import Tooltip from "components/common/tooltip";
import { DEBOUNCE_DELAY, initialMetaForTable } from "constants/common";
import AddExpenseCategoryForm from "./AddExpenseCategoryForm";
import CustomModal from "components/common/modal";
import {
  createExpenseCategory,
  getExpenseCategories,
  updateExpenseCategory,
  deleteExpenseCategory,
} from "containers/configuration/api";
import {
  capitalizeName,
  getUserRole,
  canViewOnlyConfiguration,
  isAdminUser,
  highlightFirstMatch,
} from "utils/common";
import debounce from "lodash/debounce";

const Configuration = () => {
  const [search, setSearch] = useState("");
  const [selectedConfigration, setSelectedConfirgration] = useState(0);
  const [isExpenseModelVisible, setIsExpenseModelVisible] = useState(false);
  const [currentExpenseEdit, setCurrentExpenseEdit] = useState(null);
  const [expenseCategoryList, setExpenseCategoryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [meta, setMeta] = useState({
    ...initialMetaForTable,
    mif_status: "active",
    perPage: 10,
  });

  const userRole = getUserRole();
  const isViewOnly = canViewOnlyConfiguration(userRole);
  const hasAdminFlag = isAdminUser();
  const isAdminRole = userRole === "Admin";

  const fetchExpenseCategories = async (payload) => {
    setLoading(true);
    try {
      const response = await getExpenseCategories(payload);
      if (response?.expense_categories) {
        setExpenseCategoryList(response.expense_categories);
        setTotalCount(response.meta?.total_count || 0);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetSearchQuery = (value) => {
    setMeta((prev) => ({
      ...prev,
      search: value,
      page: 1,
    }));
  };

  const debouncedSearch = useCallback(
    debounce(handleSetSearchQuery, DEBOUNCE_DELAY),
    [],
  );

  useEffect(() => {
    const payload = {
      ...meta,
      order: meta.order,
      sort: meta.sort,
    };

    fetchExpenseCategories(payload);
  }, [meta]);

  const deleteSelectedExpenseCategory = async (id) => {
    try {
      await deleteExpenseCategory(id);
      fetchExpenseCategories(meta);
    } catch (error) {
      console.log(error);
    }
  };

  const handleExpenseCategorySubmission = async (data) => {
    try {
      const isEditMode = Boolean(currentExpenseEdit);
      let response;
      if (isEditMode) {
        response = await updateExpenseCategory(
          currentExpenseEdit?.id,
          data,
          currentExpenseEdit.id,
        );
      } else {
        response = await createExpenseCategory(data);
      }

      if (response) {
        setIsExpenseModelVisible(false);
        setCurrentExpenseEdit(null);
        fetchExpenseCategories(meta);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handlePageChange = (page) => {
    setMeta((prev) => ({ ...prev, page }));
  };

  return (
    <>
      <TableWrapper
        searchPlaceholder="Search Expense Category"
        searchValue={search}
        setSearhQuery={(query) => {
          setSearch(query);
          debouncedSearch(query);
        }}
        totalListCount={totalCount}
        pageSize={meta.perPage}
        currentPage={meta.page}
        onPageChange={handlePageChange}
        isLoading={loading}
        meta={meta}
        setMeta={setMeta}
        handleRefreshPage={fetchExpenseCategories}
        actionButtons={
          // View-only users (CS Manager, Executive, Accounting without admin flag) see no Add button
          // Admin role sees no Add button (only Edit)
          // Users with admin flag get Edit from admin permissions, but not Add (admin doesn't get Add)
          isViewOnly || !hasAdminFlag
            ? [] // No Add button for view-only users, Admin role, or users with admin flag
            : selectedConfigration > 0
            ? []
            : [
                {
                  label: "Add Expense Category",
                  classes: "primary-btn",
                  icon: <Plus size={24} className="me-3" />,
                  handleClick: () => setIsExpenseModelVisible(true),
                },
              ]
        }
      >
        <div className="container-fluid">
          <div className="overflow-scroll border-radius-20px">
            <table className="table table-rows">
              <thead
                className="table-header"
                style={{
                  backgroundColor: "black",
                  color: "white",
                }}
              >
                <tr>
                  <th>Expense Category</th>
                  <th>Housing Navigation Auth</th>
                  <th>Housing Deposit Auth</th>
                  <th>Unit Required For Auth</th>
                  <th>Calculation</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {expenseCategoryList && expenseCategoryList.length > 0 ? (
                  expenseCategoryList.map((item, index) => (
                    <tr key={index}>
                      <td className="main-heading">
                        {highlightFirstMatch(item.name, meta.search)}
                      </td>

                      <td>
                        {item.housing_auth_state_id &&
                        item.housing_auth_county_ids ? (
                          <div>
                            <div>
                              <strong>State: </strong>
                              {capitalizeName(item.housing_auth_state.name)}
                            </div>
                            <div>
                              <strong>County: </strong>
                              {item.housing_auth_counties
                                .map((item) => capitalizeName(item.name))
                                .join(",")}
                            </div>
                          </div>
                        ) : (
                          <span
                            style={{
                              color: "#BDBDBD",
                            }}
                          >
                            Not Required
                          </span>
                        )}
                      </td>

                      <td>
                        {item.housing_deposit_state_id &&
                        item.housing_deposit_county_ids ? (
                          <div>
                            <div>
                              <strong>State: </strong>
                              {capitalizeName(item.housing_deposit_state.name)}
                            </div>
                            <div>
                              <strong>County: </strong>
                              {item.housing_deposit_counties
                                .map((item) => capitalizeName(item.name))
                                .join(",")}
                            </div>
                          </div>
                        ) : (
                          <span
                            style={{
                              color: "#BDBDBD",
                            }}
                          >
                            Not Required
                          </span>
                        )}
                      </td>

                      <td>
                        {item.unit_required_state_id &&
                        item.unit_required_county_ids ? (
                          <div>
                            <div>
                              <strong>State: </strong>
                              {capitalizeName(item.unit_required_state.name)}
                            </div>
                            <div>
                              <strong>County: </strong>
                              {item.unit_required_counties
                                .map((item) => capitalizeName(item.name))
                                .join(",")}
                            </div>
                          </div>
                        ) : (
                          <span
                            style={{
                              color: "#BDBDBD",
                            }}
                          >
                            Not Required
                          </span>
                        )}
                      </td>

                      <td>{item.calculation || "-"}</td>

                      <td>
                        {/* View-only users don't see Edit/Delete */}
                        {/* Users with admin flag get Edit from admin permissions */}
                        {/* Admin role only sees Edit (not Delete) */}
                        {(!isViewOnly || hasAdminFlag || isAdminRole) && (
                          <>
                            <Tooltip title="Edit">
                              <Pencil
                                size={24}
                                className="opacity-50 me-4"
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  setCurrentExpenseEdit(item);
                                  setIsExpenseModelVisible(true);
                                }}
                              />
                            </Tooltip>
                            {/* Admin role and users with admin flag don't see Delete */}
                            {!isAdminRole && !hasAdminFlag && (
                              <Tooltip title="Delete">
                                <Trash
                                  size={24}
                                  className="opacity-50 me-4"
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    deleteSelectedExpenseCategory(item.id)
                                  }
                                />
                              </Tooltip>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-3 text-gray-500">
                      No Expense Categories Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </TableWrapper>

      {isExpenseModelVisible && (
        <CustomModal
          size="sm"
          show={isExpenseModelVisible}
          onHide={() => {
            setIsExpenseModelVisible(false);
            setCurrentExpenseEdit(null);
          }}
          heading={
            currentExpenseEdit
              ? "Edit Expense Category"
              : "Add Expense Category"
          }
        >
          <AddExpenseCategoryForm
            handleExpenseSubmission={handleExpenseCategorySubmission}
            currentExpenseEdit={currentExpenseEdit}
            handleClose={() => {
              setIsExpenseModelVisible(false);
              setCurrentExpenseEdit(null);
            }}
          />
        </CustomModal>
      )}

      {/* {isEditModalVisible && (
        <CustomModal
          size="sm"
          show
          onHide={() => {
            setIsEditModalVisible(false);
            setSelectedExpense(null);
          }}
          heading={`Edit Expense Category`}
        >
          <AddExpenseCategoryForm
            initialData={selectedExpense}
            handleQuestionSubmittion={handleEditSubmission}
            handleClose={() => {
              setIsEditModalVisible(false);
              setSelectedExpense(null);
            }}
            isEditMode={true}
          />
        </CustomModal>
      )} */}
    </>
  );
};

export default Configuration;
